import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { fetchRoles, createRole, updateRole, deleteRole } from '../../store/slices/rolesSlice';
import { rolesAPI } from '../../services/api';
import SearchableSelect from '../../components/SearchableSelect';
import MultiLangInput from '../../components/MultiLangInput';
import { getLocalized } from '../../i18n/getLocalized';
import { groupName } from '../../utils/userGroupDisplay';
import { toast } from 'react-toastify';
import {
  FiShield, FiGrid, FiList, FiSearch, FiChevronDown,
  FiFileText, FiUsers, FiSettings, FiBarChart2, FiGitBranch,
  FiLock, FiPlus, FiEdit2, FiTrash2, FiX, FiUserCheck, FiLayers,
  FiCheck, FiFilter, FiFolder, FiPenTool, FiShare2,
  FiBookOpen, FiDatabase, FiCpu, FiCreditCard,
  FiHome, FiBriefcase, FiTag, FiInfo, FiArrowLeft, FiRefreshCw,
} from 'react-icons/fi';
import PageHeader from '../../components/PageHeader/PageHeader';
import { TEMPLATE_LIBRARY_ENABLED } from '../../config/features';
import './Permissions.scss';

// ─── Permission Categories ────────────────────────────────────────────────────
// Comprehensive RBAC covering every gated action in the platform.
const PERMISSION_CATEGORIES = [
  {
    key: 'contracts',
    labelKey: 'permissions.categories.contracts',
    Icon: FiFileText,
    permissions: [
      { key: 'view_contracts',      labelKey: 'permissions.permLabels.view_contracts',      descKey: 'permissions.permDescs.view_contracts' },
      { key: 'access_all_contracts', labelKey: 'permissions.permLabels.access_all_contracts', descKey: 'permissions.permDescs.access_all_contracts' },
      { key: 'access_company_contracts', labelKey: 'permissions.permLabels.access_company_contracts', descKey: 'permissions.permDescs.access_company_contracts' },
      { key: 'create_contract',     labelKey: 'permissions.permLabels.create_contract',     descKey: 'permissions.permDescs.create_contract' },
      { key: 'edit_contract',       labelKey: 'permissions.permLabels.edit_contract',       descKey: 'permissions.permDescs.edit_contract' },
      { key: 'delete_contract',     labelKey: 'permissions.permLabels.delete_contract',     descKey: 'permissions.permDescs.delete_contract' },
      { key: 'download_documents',  labelKey: 'permissions.permLabels.download_documents',  descKey: 'permissions.permDescs.download_documents' },
    ],
  },
  {
    // Sözleşme Güncelleme — the Update menu shown on a contract whose approval
    // run has completed. The menu stays creator-only; these decide which of
    // its four actions the creator is actually allowed to run.
    key: 'contractUpdate',
    labelKey: 'permissions.categories.contractUpdate',
    Icon: FiRefreshCw,
    permissions: [
      { key: 'update_contract_amendment',   labelKey: 'permissions.permLabels.update_contract_amendment',   descKey: 'permissions.permDescs.update_contract_amendment' },
      { key: 'update_contract_addendum',    labelKey: 'permissions.permLabels.update_contract_addendum',    descKey: 'permissions.permDescs.update_contract_addendum' },
      { key: 'update_contract_responsible', labelKey: 'permissions.permLabels.update_contract_responsible', descKey: 'permissions.permDescs.update_contract_responsible' },
      { key: 'add_supporting_documents',    labelKey: 'permissions.permLabels.add_supporting_documents',    descKey: 'permissions.permDescs.add_supporting_documents' },
    ],
  },
  {
    key: 'workflows',
    labelKey: 'permissions.categories.workflows',
    Icon: FiGitBranch,
    permissions: [
      { key: 'approve_contract',    labelKey: 'permissions.permLabels.approve_contract',    descKey: 'permissions.permDescs.approve_contract' },
      { key: 'manage_workflows',    labelKey: 'permissions.permLabels.manage_workflows',    descKey: 'permissions.permDescs.manage_workflows' },
    ],
  },
  {
    key: 'obligations',
    labelKey: 'permissions.categories.obligations',
    Icon: FiUserCheck,
    permissions: [
      { key: 'manage_obligations',  labelKey: 'permissions.permLabels.manage_obligations',  descKey: 'permissions.permDescs.manage_obligations' },
    ],
  },
  {
    key: 'documents',
    labelKey: 'permissions.categories.documents',
    Icon: FiFolder,
    permissions: [
      { key: 'view_document_library', labelKey: 'permissions.permLabels.view_document_library', descKey: 'permissions.permDescs.view_document_library' },
      { key: 'view_document_library_all', labelKey: 'permissions.permLabels.view_document_library_all', descKey: 'permissions.permDescs.view_document_library_all' },
      { key: 'view_document_library_company', labelKey: 'permissions.permLabels.view_document_library_company', descKey: 'permissions.permDescs.view_document_library_company' },
      { key: 'create_folders', labelKey: 'permissions.permLabels.create_folders', descKey: 'permissions.permDescs.create_folders' },
      // upload_documents gates BOTH contract attachments and library uploads;
      // it lives here next to create_folders so the folder/upload split the
      // library manages is visible in one place.
      { key: 'upload_documents', labelKey: 'permissions.permLabels.upload_documents', descKey: 'permissions.permDescs.upload_documents' },
    ],
  },
  {
    key: 'templates',
    labelKey: 'permissions.categories.templates',
    Icon: FiBookOpen,
    permissions: [
      { key: 'manage_templates',           labelKey: 'permissions.permLabels.manage_templates',           descKey: 'permissions.permDescs.manage_templates' },
      { key: 'manage_contract_templates',  labelKey: 'permissions.permLabels.manage_contract_templates',  descKey: 'permissions.permDescs.manage_contract_templates' },
      { key: 'manage_field_schemas',       labelKey: 'permissions.permLabels.manage_field_schemas',       descKey: 'permissions.permDescs.manage_field_schemas' },
      ...(TEMPLATE_LIBRARY_ENABLED ? [
        { key: 'manage_template_library',  labelKey: 'permissions.permLabels.manage_template_library',    descKey: 'permissions.permDescs.manage_template_library' },
        { key: 'view_template_library',    labelKey: 'permissions.permLabels.view_template_library',      descKey: 'permissions.permDescs.view_template_library' },
      ] : []),
    ],
  },
  {
    key: 'masterData',
    labelKey: 'permissions.categories.masterData',
    Icon: FiDatabase,
    // Parametre bazlı yetkilendirme: one manage permission per definition
    // screen under Sözleşme Ayarları > Tanımlar.
    permissions: [
      { key: 'manage_contract_main_groups', labelKey: 'permissions.permLabels.manage_contract_main_groups', descKey: 'permissions.permDescs.manage_contract_main_groups' },
      { key: 'manage_contract_types',       labelKey: 'permissions.permLabels.manage_contract_types',       descKey: 'permissions.permDescs.manage_contract_types' },
      { key: 'manage_counterparty_types',   labelKey: 'permissions.permLabels.manage_counterparty_types',   descKey: 'permissions.permDescs.manage_counterparty_types' },
      { key: 'manage_counterparties',       labelKey: 'permissions.permLabels.manage_counterparties',       descKey: 'permissions.permDescs.manage_counterparties' },
      { key: 'manage_currencies',           labelKey: 'permissions.permLabels.manage_currencies',           descKey: 'permissions.permDescs.manage_currencies' },
      { key: 'manage_supporting_doc_types', labelKey: 'permissions.permLabels.manage_supporting_doc_types', descKey: 'permissions.permDescs.manage_supporting_doc_types' },
    ],
  },
  {
    // Human Resources — user & organisation structure only. Role/permission
    // administration deliberately lives in accessCompliance below.
    key: 'users',
    labelKey: 'permissions.categories.users',
    Icon: FiUsers,
    permissions: [
      { key: 'manage_users',            labelKey: 'permissions.permLabels.manage_users',            descKey: 'permissions.permDescs.manage_users' },
      { key: 'manage_user_groups',      labelKey: 'permissions.permLabels.manage_user_groups',      descKey: 'permissions.permDescs.manage_user_groups' },
      { key: 'manage_departments',      labelKey: 'permissions.permLabels.manage_departments',      descKey: 'permissions.permDescs.manage_departments' },
      { key: 'manage_positions_titles', labelKey: 'permissions.permLabels.manage_positions_titles', descKey: 'permissions.permDescs.manage_positions_titles' },
      { key: 'manage_companies',        labelKey: 'permissions.permLabels.manage_companies',        descKey: 'permissions.permDescs.manage_companies' },
    ],
  },
  {
    // Access & Compliance — role administration (create/edit/delete roles and
    // grant/revoke their permissions), audit trail, signature integrations.
    // The permission catalogue itself is system-defined (view-only): seeing it
    // and assigning it to roles are both covered by manage_roles.
    key: 'accessCompliance',
    labelKey: 'permissions.categories.accessCompliance',
    Icon: FiLock,
    permissions: [
      { key: 'manage_roles',                  labelKey: 'permissions.permLabels.manage_roles',                  descKey: 'permissions.permDescs.manage_roles' },
      { key: 'access_audit_logs',             labelKey: 'permissions.permLabels.access_audit_logs',             descKey: 'permissions.permDescs.access_audit_logs' },
      { key: 'manage_signature_integrations', labelKey: 'permissions.permLabels.manage_signature_integrations', descKey: 'permissions.permDescs.manage_signature_integrations' },
      { key: 'reassign_tasks',                labelKey: 'permissions.permLabels.reassign_tasks',                descKey: 'permissions.permDescs.reassign_tasks' },
    ],
  },
  {
    key: 'signing',
    labelKey: 'permissions.categories.signing',
    Icon: FiPenTool,
    permissions: [
      { key: 'send_for_signature',            labelKey: 'permissions.permLabels.send_for_signature',            descKey: 'permissions.permDescs.send_for_signature' },
      { key: 'cancel_signature',              labelKey: 'permissions.permLabels.cancel_signature',              descKey: 'permissions.permDescs.cancel_signature' },
    ],
  },
  {
    key: 'externalReview',
    labelKey: 'permissions.categories.externalReview',
    Icon: FiShare2,
    permissions: [
      { key: 'manage_external_review', labelKey: 'permissions.permLabels.manage_external_review', descKey: 'permissions.permDescs.manage_external_review' },
    ],
  },
  {
    key: 'ai',
    labelKey: 'permissions.categories.ai',
    Icon: FiCpu,
    permissions: [
      { key: 'use_ai_tools', labelKey: 'permissions.permLabels.use_ai_tools', descKey: 'permissions.permDescs.use_ai_tools' },
    ],
  },
  {
    key: 'reports',
    labelKey: 'permissions.categories.reports',
    Icon: FiBarChart2,
    permissions: [
      { key: 'access_reports', labelKey: 'permissions.permLabels.access_reports', descKey: 'permissions.permDescs.access_reports' },
      { key: 'view_activity_report',    labelKey: 'permissions.permLabels.view_activity_report',    descKey: 'permissions.permDescs.view_activity_report' },
      { key: 'view_all_activity_data',  labelKey: 'permissions.permLabels.view_all_activity_data',  descKey: 'permissions.permDescs.view_all_activity_data' },
    ],
  },
  {
    key: 'admin',
    labelKey: 'permissions.categories.admin',
    Icon: FiSettings,
    permissions: [
      { key: 'manage_settings',   labelKey: 'permissions.permLabels.manage_settings',   descKey: 'permissions.permDescs.manage_settings' },
      { key: 'manage_branding',   labelKey: 'permissions.permLabels.manage_branding',   descKey: 'permissions.permDescs.manage_branding' },
      { key: 'impersonate_users', labelKey: 'permissions.permLabels.impersonate_users', descKey: 'permissions.permDescs.impersonate_users' },
    ],
  },
  {
    key: 'billing',
    labelKey: 'permissions.categories.billing',
    Icon: FiCreditCard,
    permissions: [
      { key: 'manage_subscription', labelKey: 'permissions.permLabels.manage_subscription', descKey: 'permissions.permDescs.manage_subscription' },
    ],
  },
];

const ALL_PERM_KEYS = PERMISSION_CATEGORIES.flatMap(c => c.permissions.map(p => p.key));

// ─── Member categories (who inherits this role) ──────────────────────────────
// Users matching ANY selected entity inherit the role and its permissions.
const MEMBER_CATEGORIES = [
  { key: 'organizationUnits', Icon: FiHome,      color: '#3b82f6', labelKey: 'permissions.members.organizationUnits', hintKey: 'permissions.members.organizationUnitsHint', addKey: 'permissions.members.addOrganizationUnit' },
  { key: 'positions',         Icon: FiBriefcase, color: '#8b5cf6', labelKey: 'permissions.members.positions',         hintKey: 'permissions.members.positionsHint',         addKey: 'permissions.members.addPosition' },
  { key: 'titles',            Icon: FiTag,       color: '#10b981', labelKey: 'permissions.members.titles',            hintKey: 'permissions.members.titlesHint',            addKey: 'permissions.members.addTitle' },
  { key: 'userGroups',        Icon: FiUsers,     color: '#f59e0b', labelKey: 'permissions.members.userGroups',        hintKey: 'permissions.members.userGroupsHint',        addKey: 'permissions.members.addUserGroup' },
];

const EMPTY_MEMBERS = { organizationUnits: [], positions: [], titles: [], userGroups: [] };

// Populated refs from the API may be objects; the form keeps raw ids
const toMemberIds = (arr) => (arr || []).map(v => (v && v._id ? v._id : v)).filter(Boolean);
const membersFromRole = (role) => ({
  organizationUnits: toMemberIds(role?.members?.organizationUnits),
  positions: toMemberIds(role?.members?.positions),
  titles: toMemberIds(role?.members?.titles),
  userGroups: toMemberIds(role?.members?.userGroups),
});

// ─── Monochrome Donut Ring ────────────────────────────────────────────────────
function DonutRing({ value, total, size = 60, stroke = 5 }) {
  const pct = total > 0 ? Math.min(1, value / total) : 0;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const offset = c * (1 - pct);

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
      <circle
        className="role-card__ring-track"
        cx={size / 2} cy={size / 2} r={r}
        fill="none" strokeWidth={stroke}
      />
      <circle
        className="role-card__ring-fill"
        cx={size / 2} cy={size / 2} r={r}
        fill="none" strokeWidth={stroke}
        strokeLinecap="round"
        strokeDasharray={c}
        strokeDashoffset={offset}
      />
    </svg>
  );
}

// ─── Category Filter Dropdown ─────────────────────────────────────────────────
function CategoryFilter({ categories, active, onToggle, onSelectAll, onClear, t }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const total = categories.length;
  const activeCount = active.size;
  const allOn = activeCount === total;
  const label = allOn
    ? t('permissions.allCategories')
    : t('permissions.someCategories', { count: activeCount, total });

  return (
    <div className="perm-filter" ref={ref}>
      <button
        type="button"
        className={`perm-filter__btn${open ? ' is-open' : ''}${!allOn ? ' is-dirty' : ''}`}
        onClick={() => setOpen(o => !o)}
        aria-haspopup="true"
        aria-expanded={open}
      >
        <FiFilter size={13} />
        <span>{label}</span>
        <FiChevronDown
          size={12}
          className={`perm-filter__chev${open ? ' is-open' : ''}`}
        />
      </button>
      {open && (
        <div className="perm-filter__pop" role="menu">
          <div className="perm-filter__pop-head">
            <span>{t('permissions.categoriesLabel')}</span>
            <div className="perm-filter__pop-actions">
              <button type="button" onClick={onSelectAll} disabled={allOn}>{t('permissions.filterAll')}</button>
              <button type="button" onClick={onClear} disabled={activeCount === 0}>{t('permissions.filterNone')}</button>
            </div>
          </div>
          <div className="perm-filter__list">
            {categories.map(cat => {
              const checked = active.has(cat.key);
              return (
                <button
                  type="button"
                  key={cat.key}
                  className={`perm-filter__item${checked ? ' is-on' : ''}`}
                  onClick={() => onToggle(cat.key)}
                  role="menuitemcheckbox"
                  aria-checked={checked}
                >
                  <span className={`perm-filter__box${checked ? ' is-on' : ''}`} aria-hidden="true">
                    {checked && <FiCheck size={11} strokeWidth={3.5} />}
                  </span>
                  <cat.Icon size={13} />
                  <span className="perm-filter__name">{t(cat.labelKey)}</span>
                  <span className="perm-filter__count">{cat.permissions.length}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────
export default function Permissions() {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  // System role names/descriptions are { en, tr } maps; custom roles are plain
  // strings. Resolve to the active UI language everywhere they render.
  const roleName = (role) => getLocalized(role?.name, i18n.language);
  const roleDesc = (role) => getLocalized(role?.description, i18n.language);
  const { items: roles, loading } = useSelector(s => s.roles);
  const [saving, setSaving] = useState(false);

  const [view, setView]         = useState('matrix');
  const [search, setSearch]     = useState('');
  const [activeCats, setActiveCats] = useState(() => new Set(PERMISSION_CATEGORIES.map(c => c.key)));
  const [expanded, setExpanded] = useState(
    Object.fromEntries(PERMISSION_CATEGORIES.map(c => [c.key, true]))
  );
  const [editorOpen, setEditorOpen]   = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', permissions: [], members: EMPTY_MEMBERS });
  const [hoveredRoleId, setHoveredRoleId] = useState(null);
  // Inline matrix editing: one role column at a time flips into edit mode and
  // its cells become toggles; Save/Cancel live in the role header.
  const [matrixEditRoleId, setMatrixEditRoleId] = useState(null);
  const [matrixDraft, setMatrixDraft] = useState([]);
  const [matrixSaving, setMatrixSaving] = useState(false);
  const [editorTab, setEditorTab] = useState('permissions'); // 'permissions' | 'members'
  const [memberOptions, setMemberOptions] = useState(null);  // { organizationUnits, positions, titles, userGroups }

  const searchInputRef = useRef(null);

  useEffect(() => { dispatch(fetchRoles()); }, [dispatch]);

  // Lazy-load member option lists the first time the editor opens
  useEffect(() => {
    if (!editorOpen || memberOptions) return;
    rolesAPI.memberOptions()
      .then(res => setMemberOptions(res.data))
      .catch(() => setMemberOptions(EMPTY_MEMBERS));
  }, [editorOpen, memberOptions]);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
      if (e.key === 'Escape' && editorOpen) setEditorOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [editorOpen]);

  const stats = useMemo(() => ({
    totalRoles:  roles.length,
    systemRoles: roles.filter(r => r.isSystem).length,
    customRoles: roles.filter(r => !r.isSystem).length,
    totalPerms:  ALL_PERM_KEYS.length,
  }), [roles]);

  const filteredCategories = useMemo(() => {
    const q = search.trim().toLowerCase();
    return PERMISSION_CATEGORIES
      .filter(cat => activeCats.has(cat.key))
      .map(cat => ({
        ...cat,
        permissions: q
          ? cat.permissions.filter(p =>
              t(p.labelKey).toLowerCase().includes(q) || t(p.descKey).toLowerCase().includes(q)
            )
          : cat.permissions,
      }))
      .filter(cat => cat.permissions.length > 0);
  }, [search, t, activeCats]);

  const toggleCatFilter = useCallback((key) => {
    setActiveCats(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const selectAllCats = useCallback(() => {
    setActiveCats(new Set(PERMISSION_CATEGORIES.map(c => c.key)));
  }, []);

  const clearCats = useCallback(() => {
    setActiveCats(new Set());
  }, []);

  // ── Editor helpers ───────────────────────────────────────────────────────────
  const openCreate = () => {
    setEditingRole(null);
    setForm({ name: '', description: '', permissions: [], members: EMPTY_MEMBERS });
    setEditorTab('permissions');
    setEditorOpen(true);
  };

  const openEdit = (role) => {
    setEditingRole(role);
    setForm({
      name: role.name,
      description: role.description || '',
      permissions: [...(role.permissions || [])],
      members: membersFromRole(role),
    });
    setEditorTab('permissions');
    setEditorOpen(true);
  };

  const togglePerm = (key) => {
    setForm(f => ({
      ...f,
      permissions: f.permissions.includes(key)
        ? f.permissions.filter(p => p !== key)
        : [...f.permissions, key],
    }));
  };

  const toggleCatAll = (catKey) => {
    const keys = PERMISSION_CATEGORIES.find(c => c.key === catKey)?.permissions.map(p => p.key) || [];
    const allHas = keys.every(k => form.permissions.includes(k));
    setForm(f => ({
      ...f,
      permissions: allHas
        ? f.permissions.filter(p => !keys.includes(p))
        : [...new Set([...f.permissions, ...keys])],
    }));
  };

  // ── Member selection helpers ────────────────────────────────────────────────
  const addMember = (catKey, id) => {
    setForm(f => ({
      ...f,
      members: { ...f.members, [catKey]: [...f.members[catKey], id] },
    }));
  };

  const removeMember = (catKey, id) => {
    setForm(f => ({
      ...f,
      members: { ...f.members, [catKey]: f.members[catKey].filter(x => x !== id) },
    }));
  };

  // Resolve an entity id to its display object — from the fetched option
  // lists, falling back to the populated refs on the role being edited
  const memberEntityMap = useMemo(() => {
    const map = {};
    const absorb = (arr) => (arr || []).forEach(o => { if (o && o._id) map[o._id] = o; });
    // Role refs first so the option lists — the fuller records — win on
    // overlap; ids only the role knows about still resolve through them.
    if (editingRole?.members) Object.values(editingRole.members).forEach(absorb);
    if (memberOptions) Object.values(memberOptions).forEach(absorb);
    return map;
  }, [memberOptions, editingRole]);

  const selectedMemberCategories = MEMBER_CATEGORIES
    .filter(c => (form.members[c.key] || []).length > 0).length;
  const totalMemberEntities = MEMBER_CATEGORIES
    .reduce((sum, c) => sum + (form.members[c.key] || []).length, 0);

  const handleSave = async () => {
    // name is a string or an { en, tr } map — valid when any language is filled
    if (!getLocalized(form.name, i18n.language).trim()) {
      toast.error(t('permissions.messages.roleNameRequired'));
      return;
    }
    setSaving(true);
    const data = {
      name: form.name,
      description: form.description,
      permissions: form.permissions,
      members: form.members,
    };
    try {
      if (editingRole) {
        await dispatch(updateRole({ id: editingRole._id, data })).unwrap();
        toast.success(t('permissions.messages.roleUpdated'));
      } else {
        await dispatch(createRole(data)).unwrap();
        toast.success(t('permissions.messages.roleCreated'));
      }
      setEditorOpen(false);
    } catch (err) {
      toast.error(typeof err === 'string' ? err : t('permissions.messages.failedToSaveRole'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (role) => {
    if (!window.confirm(t('permissions.messages.deleteConfirm', { name: roleName(role) }))) return;
    try {
      await dispatch(deleteRole(role._id)).unwrap();
      toast.success(t('permissions.messages.roleDeleted'));
    } catch (err) {
      toast.error(typeof err === 'string' ? err : t('permissions.messages.failedToDeleteRole'));
    }
  };

  // ── Matrix inline editing ───────────────────────────────────────────────────
  const startMatrixEdit = (role) => {
    setMatrixEditRoleId(role._id);
    setMatrixDraft([...(role.permissions || [])]);
  };
  const cancelMatrixEdit = () => {
    setMatrixEditRoleId(null);
    setMatrixDraft([]);
  };
  const toggleMatrixPerm = (key) => {
    setMatrixDraft(prev => (prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]));
  };
  const saveMatrixEdit = async () => {
    if (!matrixEditRoleId) return;
    setMatrixSaving(true);
    try {
      await dispatch(updateRole({ id: matrixEditRoleId, data: { permissions: matrixDraft } })).unwrap();
      toast.success(t('permissions.messages.roleUpdated', { defaultValue: 'Role updated' }));
      cancelMatrixEdit();
    } catch (err) {
      toast.error(typeof err === 'string' ? err : t('permissions.messages.failedToSaveRole', { defaultValue: 'Failed to save role' }));
    }
    setMatrixSaving(false);
  };

  // ── Matrix View ─────────────────────────────────────────────────────────────
  const renderMatrix = () => (
    <div className="perm-matrix-wrap">
      <div className="perm-matrix">
        {/* Axis super-headers: İZİNLER over the left column, ROLLER spanning
            the role columns. */}
        <div className="perm-matrix__super">
          {/* Left super cell stays empty — the İZİNLER heading lives in the
              corner cell below, level with the role headers. */}
          <div className="perm-matrix__super-perms" aria-hidden="true" />
          <div className="perm-matrix__super-roles" style={{ minWidth: roles.length * 128 }}>
            <FiUsers size={12} />
            <span>{t('permissions.rolesAxis', { defaultValue: 'Roles' })}</span>
          </div>
        </div>

        <div className="perm-matrix__header">
          {/* Blank corner — kept only as a spacer so the permission column and
              role columns stay aligned. */}
          <div className="perm-matrix__corner" aria-hidden="true" />
          {roles.map(role => {
            const isEditing = matrixEditRoleId === role._id;
            const effPerms = isEditing ? matrixDraft : (role.permissions || []);
            const permCount = effPerms.length;
            const pct = ALL_PERM_KEYS.length > 0 ? permCount / ALL_PERM_KEYS.length : 0;
            const r = 12, c = 2 * Math.PI * r;
            return (
              <div
                key={role._id}
                className={`perm-matrix__role-th${hoveredRoleId === role._id ? ' is-hot' : ''}${isEditing ? ' is-editing' : ''}`}
                onMouseEnter={() => setHoveredRoleId(role._id)}
                onMouseLeave={() => setHoveredRoleId(null)}
              >
                <div className="perm-matrix__role-badge">
                  {(roleName(role)[0] || '?').toUpperCase()}
                </div>
                <button
                  type="button"
                  className="perm-matrix__role-name-btn"
                  onClick={() => !isEditing && startMatrixEdit(role)}
                  title={t('permissions.editInMatrix', { defaultValue: 'Edit permissions in the matrix' })}
                >
                  <span className="perm-matrix__role-name" title={roleName(role)}>{roleName(role)}</span>
                  <FiEdit2 size={11} className="perm-matrix__role-edit-ic" />
                </button>
                <div className="perm-matrix__role-meta">
                  {isEditing ? (
                    <div className="perm-matrix__role-actions">
                      <button
                        type="button"
                        className="perm-matrix__role-act perm-matrix__role-act--save"
                        onClick={saveMatrixEdit}
                        disabled={matrixSaving}
                        title={t('common.save')}
                      >
                        <FiCheck size={13} strokeWidth={3} />
                      </button>
                      <button
                        type="button"
                        className="perm-matrix__role-act perm-matrix__role-act--cancel"
                        onClick={cancelMatrixEdit}
                        disabled={matrixSaving}
                        title={t('common.cancel')}
                      >
                        <FiX size={13} strokeWidth={3} />
                      </button>
                    </div>
                  ) : (
                    role.isSystem
                      ? <span className="perm-tag perm-tag--sys">{t('permissions.systemBadge')}</span>
                      : <span className="perm-tag perm-tag--custom">{t('permissions.customBadge')}</span>
                  )}
                  <div className="perm-matrix__role-ring" aria-label={`${permCount} of ${ALL_PERM_KEYS.length}`}>
                    <svg width={30} height={30} viewBox="0 0 30 30">
                      <circle className="perm-matrix__role-ring-track" cx={15} cy={15} r={r} fill="none" strokeWidth={3} />
                      <circle
                        className="perm-matrix__role-ring-fill"
                        cx={15} cy={15} r={r} fill="none" strokeWidth={3}
                        strokeLinecap="round"
                        strokeDasharray={c}
                        strokeDashoffset={c * (1 - pct)}
                      />
                    </svg>
                    <span className="perm-matrix__role-ring-num">{permCount}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* İzinler axis bar — NOT pinned: scrolls away with the list. */}
        <div className="perm-matrix__axis-row">
          <div className="perm-matrix__corner-title">
            <FiFileText size={12} />
            <span>{t('permissions.permissionsAxis', { defaultValue: 'Permissions' })}</span>
          </div>
        </div>

        {filteredCategories.map(cat => (
          <React.Fragment key={cat.key}>
            <div
              className={`perm-matrix__cat-row${expanded[cat.key] ? ' is-open' : ''}`}
              onClick={() => setExpanded(prev => ({ ...prev, [cat.key]: !prev[cat.key] }))}
            >
              <div className="perm-matrix__cat-label">
                <cat.Icon size={14} />
                <span>{t(cat.labelKey)}</span>
                <span className="perm-matrix__cat-badge">
                  {cat.permissions.length}
                </span>
                <span className={`perm-matrix__cat-chevron${expanded[cat.key] ? ' is-open' : ''}`}>
                  <FiChevronDown size={14} />
                </span>
              </div>
              {roles.map(role => {
                const rolePerms = matrixEditRoleId === role._id ? matrixDraft : (role.permissions || []);
                const granted = cat.permissions.filter(p => rolePerms.includes(p.key)).length;
                const total = cat.permissions.length;
                const pct = total > 0 ? (granted / total) * 100 : 0;
                return (
                  <div key={role._id} className={`perm-matrix__cat-cell${matrixEditRoleId === role._id ? ' is-editing' : ''}`}>
                    <div className="perm-cat-track">
                      <div className="perm-cat-fill" style={{ width: `${pct}%` }} />
                    </div>
                    <span className={`perm-cat-ratio${granted > 0 ? ' is-on' : ''}`}>
                      {granted}/{total}
                    </span>
                  </div>
                );
              })}
            </div>

            {expanded[cat.key] && cat.permissions.map((perm, idx) => (
              <div
                key={perm.key}
                className="perm-matrix__row"
                style={{ '--d': `${Math.min(idx * 16, 160)}ms` }}
              >
                <div className="perm-matrix__perm-label" title={t(perm.descKey)}>
                  <span className="perm-label-name">{t(perm.labelKey)}</span>
                  <span className="perm-label-desc">{t(perm.descKey)}</span>
                </div>
                {roles.map(role => {
                  const isEditing = matrixEditRoleId === role._id;
                  const rolePerms = isEditing ? matrixDraft : (role.permissions || []);
                  const has = rolePerms.includes(perm.key);
                  const isHot = hoveredRoleId === role._id;
                  return (
                    <div
                      key={role._id}
                      className={`perm-matrix__cell${has ? ' perm-matrix__cell--yes' : ' perm-matrix__cell--no'}${isHot ? ' is-hot' : ''}${isEditing ? ' is-editing' : ''}`}
                    >
                      {isEditing ? (
                        <button
                          type="button"
                          className={`perm-cell-toggle${has ? ' is-on' : ''}`}
                          onClick={() => toggleMatrixPerm(perm.key)}
                          aria-pressed={has}
                          title={t(perm.labelKey)}
                        >
                          {has ? <FiCheck size={13} strokeWidth={3} /> : null}
                        </button>
                      ) : has
                        ? <span className="perm-check" style={{ '--d': `${idx * 20}ms` }}>
                            <FiCheck size={13} strokeWidth={3} />
                          </span>
                        : <span className="perm-dash" aria-hidden="true" />
                      }
                    </div>
                  );
                })}
              </div>
            ))}
          </React.Fragment>
        ))}

        {roles.length === 0 && !loading && (
          <div className="perm-matrix__empty">
            <FiShield size={36} />
            <p>{t('permissions.noRolesLoaded')}</p>
          </div>
        )}

        {filteredCategories.length === 0 && roles.length > 0 && (
          <div className="perm-matrix__empty">
            <FiSearch size={36} />
            <p>{t('permissions.noPermissionsMatch')}</p>
            <small>{t('permissions.noPermissionsMatchHint')}</small>
          </div>
        )}
      </div>
    </div>
  );

  // ── Cards View ──────────────────────────────────────────────────────────────
  const renderCards = () => (
    <div className="perm-cards">
      {roles.map((role, idx) => {
        const permCount = (role.permissions || []).length;
        const desc = roleDesc(role);
        return (
          <div
            key={role._id}
            className="role-card"
            style={{ '--d': `${Math.min(idx * 40, 280)}ms` }}
          >
            <div className="role-card__header">
              <div className="role-card__avatar">
                {(roleName(role)[0] || '?').toUpperCase()}
              </div>
              <div className="role-card__meta">
                <span className="role-card__name">{roleName(role)}</span>
                <code className="role-card__slug">{role.slug}</code>
              </div>
              <span className={`perm-tag ${role.isSystem ? 'perm-tag--sys' : 'perm-tag--custom'}`}>
                {role.isSystem ? t('permissions.systemBadge') : t('permissions.customBadge')}
              </span>
            </div>

            {desc && <p className="role-card__desc">{desc}</p>}

            <div className="role-card__coverage">
              <div className="role-card__ring">
                <DonutRing value={permCount} total={ALL_PERM_KEYS.length} size={60} stroke={5} />
                <div className="role-card__ring-label">
                  {Math.round((permCount / ALL_PERM_KEYS.length) * 100)}%
                </div>
              </div>
              <div className="role-card__coverage-text">
                <span className="role-card__coverage-num">
                  {permCount} <span>/ {ALL_PERM_KEYS.length}</span>
                </span>
                <span className="role-card__coverage-label">
                  permissions granted
                </span>
              </div>
            </div>

            <div className="role-card__breakdown">
              {PERMISSION_CATEGORIES.map(cat => {
                const total = cat.permissions.length;
                const granted = cat.permissions.filter(p => (role.permissions || []).includes(p.key)).length;
                const pct = total > 0 ? (granted / total) * 100 : 0;
                return (
                  <div key={cat.key} className="role-breakdown-row">
                    <span className="role-breakdown-label">
                      {t(cat.labelKey)}
                    </span>
                    <div className="role-breakdown-track">
                      <div
                        className="role-breakdown-fill"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <span className={`role-breakdown-ratio${granted > 0 ? ' is-on' : ''}`}>
                      {granted}/{total}
                    </span>
                  </div>
                );
              })}
            </div>

            <div className="role-card__actions">
              <button
                className="role-card__btn role-card__btn--edit"
                onClick={() => openEdit(role)}
              >
                <FiEdit2 size={13} /> {t('permissions.editRole')}
              </button>
              {/* System roles stay editable but can't be deleted (they're
                  referenced by provisioning). Custom roles can be removed. */}
              {!role.isSystem && (
                <button
                  className="role-card__btn role-card__btn--del"
                  onClick={() => handleDelete(role)}
                  aria-label={t('permissions.deleteRoleAria', { name: roleName(role) })}
                >
                  <FiTrash2 size={13} />
                </button>
              )}
            </div>
          </div>
        );
      })}

      <button
        className="role-card role-card--create"
        onClick={openCreate}
        style={{ '--d': `${Math.min(roles.length * 40, 280)}ms` }}
      >
        <div className="role-card--create__plus">
          <FiPlus size={22} />
        </div>
        <strong>{t('permissions.createCustomRole')}</strong>
        <small>{t('permissions.createCustomRoleDesc')}</small>
      </button>
    </div>
  );

  // ── Members tab (drawer) ────────────────────────────────────────────────────
  const renderMembersTab = () => (
    <section className="perm-members">
      <div className="perm-members__banner">
        <FiInfo size={14} />
        <div>
          <p>{t('permissions.members.bannerLine1')}</p>
          <p>{t('permissions.members.bannerLine2')}</p>
        </div>
      </div>

      {MEMBER_CATEGORIES.map(cat => {
        const selectedIds = form.members[cat.key] || [];
        const options = (memberOptions?.[cat.key] || [])
          .filter(o => !selectedIds.includes(o._id))
          .map(o => ({ value: o._id, label: groupName(o, t), sublabel: o.code }));

        return (
          <div key={cat.key} className="perm-member-cat" style={{ '--cat-color': cat.color }}>
            <div className="perm-member-cat__head">
              <div className="perm-member-cat__icon">
                <cat.Icon size={15} />
              </div>
              <div className="perm-member-cat__titles">
                <span className="perm-member-cat__title">{t(cat.labelKey)}</span>
                <span className="perm-member-cat__hint">{t(cat.hintKey)}</span>
              </div>
              <div className="perm-member-cat__select">
                <SearchableSelect
                  value=""
                  onChange={(id) => { if (id) addMember(cat.key, id); }}
                  options={options}
                  placeholder={t(cat.addKey)}
                  searchPlaceholder={t('permissions.members.searchPlaceholder')}
                  emptyText={!memberOptions
                    ? t('permissions.members.loadingOptions')
                    : options.length === 0
                      ? t('permissions.members.allAdded')
                      : t('permissions.members.noMatches')}
                />
              </div>
            </div>

            {selectedIds.length > 0 && (
              <div className="perm-member-cat__chips">
                {selectedIds.map(id => {
                  const entity = memberEntityMap[id];
                  return (
                    <span key={id} className="perm-member-chip">
                      {groupName(entity, t) || '…'}
                      <button
                        type="button"
                        onClick={() => removeMember(cat.key, id)}
                        aria-label={`Remove ${entity?.name || ''}`}
                      >
                        <FiX size={11} />
                      </button>
                    </span>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </section>
  );

  // ── Full-page role editor ───────────────────────────────────────────────────
  const renderEditorPage = () => (
    <div className="permissions-page perm-editor-page">
      <div className="page-header">
        <h2>
          <button
            type="button"
            className="perm-editor__back"
            onClick={() => setEditorOpen(false)}
            aria-label={t('permissions.cancel')}
          >
            <FiArrowLeft size={17} />
          </button>
          <FiShield style={{ marginRight: 8 }} />
          {editingRole ? t('permissions.editRole') : t('permissions.createRole')}
          {editingRole && <span className="perm-editor__subject">{roleName(editingRole)}</span>}
        </h2>
        <div className="actions">
          <button className="perm-btn perm-btn--ghost" onClick={() => setEditorOpen(false)}>
            {t('permissions.cancel')}
          </button>
          <button className="perm-btn perm-btn--primary" onClick={handleSave} disabled={saving}>
            {saving ? t('permissions.saving') : editingRole ? t('permissions.updateRole') : t('permissions.createRole')}
          </button>
        </div>
      </div>

      <div className="perm-editor">
        <section className="perm-editor__details">
          <h4 className="perm-drawer__section-title">{t('permissions.roleDetails')}</h4>
          <div className="perm-editor__fields">
            <div className="perm-field">
              <label>{t('permissions.roleName')} *</label>
              <MultiLangInput
                value={form.name}
                onChange={value => setForm(f => ({ ...f, name: value }))}
                primaryLang={i18n.language}
                placeholder={t('permissions.roleNamePlaceholder')}
              />
            </div>
            <div className="perm-field perm-field--grow">
              <label>{t('permissions.description')}</label>
              <MultiLangInput
                value={form.description}
                onChange={value => setForm(f => ({ ...f, description: value }))}
                primaryLang={i18n.language}
                placeholder={t('permissions.descriptionPlaceholder')}
                multiline
                rows={2}
              />
            </div>
          </div>
        </section>

        <div className="perm-drawer__tabs" role="tablist">
              <button
                type="button"
                role="tab"
                aria-selected={editorTab === 'permissions'}
                className={`perm-drawer__tab${editorTab === 'permissions' ? ' is-active' : ''}`}
                onClick={() => setEditorTab('permissions')}
              >
                <FiLock size={13} />
                {t('permissions.permissionsTitle')}
                <span className="perm-drawer__tab-count">{form.permissions.length}</span>
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={editorTab === 'members'}
                className={`perm-drawer__tab${editorTab === 'members' ? ' is-active' : ''}`}
                onClick={() => setEditorTab('members')}
              >
                <FiUsers size={13} />
                {t('permissions.members.tabTitle')}
                <span className="perm-drawer__tab-count">{totalMemberEntities}</span>
              </button>
            </div>

            {editorTab === 'members' ? renderMembersTab() : (
            <section>
              <div className="perm-drawer__perm-head">
                <span className="perm-drawer__perm-head-title">
                  <FiLayers size={12} /> {t('permissions.permissionsTitle')}
                </span>
                <span className="perm-drawer__perm-count">
                  {t('permissions.selectedCount', { selected: form.permissions.length, total: ALL_PERM_KEYS.length })}
                </span>
              </div>

              {PERMISSION_CATEGORIES.map(cat => {
                const catKeys = cat.permissions.map(p => p.key);
                const grantedCount = catKeys.filter(k => form.permissions.includes(k)).length;
                const allGranted = grantedCount === catKeys.length;
                const isOpen = expanded[cat.key];
                return (
                  <div key={cat.key} className={`perm-cat-editor${allGranted ? ' is-all' : ''}`}>
                    <div
                      className="perm-cat-editor__head"
                      onClick={() => setExpanded(prev => ({ ...prev, [cat.key]: !prev[cat.key] }))}
                    >
                      <div className="perm-cat-editor__head-title">
                        <cat.Icon size={14} />
                        <span>{t(cat.labelKey)}</span>
                        <span className="perm-cat-editor__badge">
                          {grantedCount}/{catKeys.length}
                        </span>
                      </div>
                      <button
                        className={`perm-cat-editor__toggle-all${allGranted ? ' perm-cat-editor__toggle-all--all' : ''}`}
                        onClick={(e) => { e.stopPropagation(); toggleCatAll(cat.key); }}
                      >
                        {allGranted ? t('permissions.deselectAll') : t('permissions.selectAll')}
                      </button>
                      <span className={`perm-cat-editor__chevron${isOpen ? ' is-open' : ''}`}>
                        <FiChevronDown size={14} />
                      </span>
                    </div>

                    <div className={`perm-cat-editor__body${isOpen ? '' : ' is-closed'}`}>
                      <div>
                        {cat.permissions.map(perm => {
                          const on = form.permissions.includes(perm.key);
                          return (
                            <label
                              key={perm.key}
                              className={`perm-toggle${on ? ' perm-toggle--on' : ''}`}
                            >
                              <div className="perm-toggle__info">
                                <span className="perm-toggle__name">{t(perm.labelKey)}</span>
                                <span className="perm-toggle__desc">{t(perm.descKey)}</span>
                              </div>
                              <div
                                className={`perm-toggle__sw${on ? ' perm-toggle__sw--on' : ''}`}
                                onClick={(e) => { e.preventDefault(); togglePerm(perm.key); }}
                                role="switch"
                                aria-checked={on}
                                tabIndex={0}
                                onKeyDown={(e) => {
                                  if (e.key === ' ' || e.key === 'Enter') {
                                    e.preventDefault();
                                    togglePerm(perm.key);
                                  }
                                }}
                              >
                                <div className="perm-toggle__thumb" />
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              })}
            </section>
            )}

        <div className="perm-editor__statbar">
          {editorTab === 'members' ? (
            <span className="perm-drawer__footer-stat">
              <FiUsers size={12} />
              <strong>{selectedMemberCategories}</strong> {t('permissions.members.categoriesSelected')}
            </span>
          ) : (
            <span className="perm-drawer__footer-stat">
              <FiUserCheck size={12} />
              <strong>{form.permissions.length}</strong> of {ALL_PERM_KEYS.length} selected
            </span>
          )}
        </div>
      </div>
    </div>
  );

  // ── Loading skeleton ────────────────────────────────────────────────────────
  const renderSkeleton = () => (
    <div className="perm-loading" aria-busy="true">
      {[0, 1, 2, 3, 4, 5].map(i => (
        <div key={i} className="perm-skeleton" style={{ animationDelay: `${i * 80}ms` }} />
      ))}
    </div>
  );

  // Counter strip: "10 Roles (8 System · 2 Custom) │ 35 Permissions".
  // Roles and Permissions are the two headline figures; System and Custom are a
  // breakdown *of* the role count, so they sit in brackets and render muted —
  // as four equal chips they read as four unrelated totals.
  // The nouns are translated and count-aware (English pluralizes, Turkish keeps
  // the singular after a numeral).

  // ── Main render ─────────────────────────────────────────────────────────────
  if (editorOpen) return renderEditorPage();

  return (
    <div className="permissions-page">
      <PageHeader
        label={t('nav.accessCompliance', 'Access & Compliance')}
        labelIcon={<FiShield />}
        title={t('permissions.title')}
        description={t('pageDescriptions.permissions')}
        actions={(
          <button className="btn btn-primary btn-sm" onClick={openCreate}>
            <FiPlus size={14} /> {t('permissions.newRole')}
          </button>
        )}
      />

      <div className="perm-metrics" role="list">
        <div className="perm-metrics__item" role="listitem" style={{ '--d': '0ms' }}>
          <FiUsers size={13} />
          <strong>{stats.totalRoles}</strong>
          <span>{t('permissions.metrics.roles', { count: stats.totalRoles })}</span>
          <span className="perm-metrics__breakdown">
            ({stats.systemRoles} {t('permissions.metrics.system')}
            {' · '}
            {stats.customRoles} {t('permissions.metrics.custom')})
          </span>
        </div>

        <span className="perm-metrics__divider" aria-hidden="true" />

        <div className="perm-metrics__item" role="listitem" style={{ '--d': '40ms' }}>
          <FiLock size={13} />
          <strong>{stats.totalPerms}</strong>
          <span>{t('permissions.metrics.permissions', { count: stats.totalPerms })}</span>
        </div>
      </div>

      <div className="perm-toolbar">
        <div className="perm-toolbar__search">
          <FiSearch size={15} />
          <input
            ref={searchInputRef}
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={t('permissions.searchPermissions')}
          />
          {search ? (
            <button
              className="perm-toolbar__clear"
              onClick={() => setSearch('')}
              aria-label="Clear search"
            >
              <FiX size={12} />
            </button>
          ) : (
            <span className="perm-toolbar__kbd" title="Focus search">⌘K</span>
          )}
        </div>

        {view === 'matrix' && (
          <CategoryFilter
            categories={PERMISSION_CATEGORIES}
            active={activeCats}
            onToggle={toggleCatFilter}
            onSelectAll={selectAllCats}
            onClear={clearCats}
            t={t}
          />
        )}

        <div className="perm-toolbar__spacer" />

        <div className="perm-toolbar__view" role="tablist" aria-label="View mode">
          <button
            className={`perm-view-btn${view === 'matrix' ? ' perm-view-btn--active' : ''}`}
            onClick={() => setView('matrix')}
            role="tab"
            aria-selected={view === 'matrix'}
          >
            <FiGrid size={14} /> {t('permissions.matrixTab')}
          </button>
          <button
            className={`perm-view-btn${view === 'cards' ? ' perm-view-btn--active' : ''}`}
            onClick={() => setView('cards')}
            role="tab"
            aria-selected={view === 'cards'}
          >
            <FiList size={14} /> {t('permissions.rolesTab')}
          </button>
        </div>
      </div>

      {loading
        ? renderSkeleton()
        : view === 'matrix' ? renderMatrix() : renderCards()
      }
    </div>
  );
}
